import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { MenuModule } from 'primeng/menu';
import { Menu } from 'primeng/menu';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { DataService, Note } from '../../_service/data.service';
import { FileUploadService, Attachment } from '../../_service/file-upload.service';
import { DataOfflineService } from '../../_service/data-offline.service';
import { removeAttachmentImagesFromHtml } from '../../_util/attachment-ref.util';
import { SnackbarService } from '../../_service/snackbar.service';
import { SpaceDirectoryService } from '../../_service/space-directory.service';
import { AuthService } from '../../_service/auth.service';

interface AttachmentRow {
  attachmentId: string;
  filename: string;
  size: number;
  mimeType: string;
  noteId: string;
  spaceId?: string;
  noteTitle: string;
  /** Upload date (ISO), '' if unknown. */
  createdOn: string;
  spaceName: string;
  /** Notes carry no owner of their own: a note belongs to the owner of the Space it's in. */
  owner: string;
}

type SortColumn = 'filename' | 'size' | 'noteTitle' | 'createdOn' | 'spaceName' | 'owner';
type SortDirection = 'asc' | 'desc';

@Component({
  selector: 'app-manage-attachments',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    ConfirmDialogModule,
    TooltipModule,
    MenuModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './manage-attachments.component.html',
})
export class ManageAttachmentsComponent implements OnInit {
  @ViewChild('rowMenu') rowMenu!: Menu;

  rows: AttachmentRow[] = [];
  filteredRows: AttachmentRow[] = [];
  loading = false;

  searchText = '';
  sortColumn: SortColumn = 'filename';
  sortDir: SortDirection = 'asc';

  activeRow: AttachmentRow | null = null;
  menuItems: MenuItem[] = [];

  showRenameDialog = false;
  renameValue = '';
  renameErrorMsg = '';
  renaming = false;

  constructor(
    private router: Router,
    private dataService: DataService,
    private fileUploadService: FileUploadService,
    private dataOffline: DataOfflineService,
    private snackbar: SnackbarService,
    private confirmationService: ConfirmationService,
    private spaceDirectory: SpaceDirectoryService,
    private auth: AuthService,
  ) {}

  ngOnInit(): void {
    this.buildMenu();
    this.loadAttachments();
  }

  private buildMenu(): void {
    this.menuItems = [
      {
        label: 'Download',
        icon: 'pi pi-download',
        command: () => this.activeRow && this.downloadAttachment(this.activeRow),
      },
      {
        label: 'Rename',
        icon: 'pi pi-pencil',
        command: () => this.activeRow && this.openRenameDialog(this.activeRow),
      },
      { separator: true },
      {
        label: 'Delete',
        icon: 'pi pi-trash',
        styleClass: 'manage-attachments-menu-delete',
        command: () => this.activeRow && this.confirmDelete(this.activeRow),
      },
    ];
  }

  loadAttachments(): void {
    this.loading = true;
    this.rows = [];
    this.filteredRows = [];

    const notesWithAttachments = (this.dataService.allNotes || []).filter(
      (n) =>
        Array.isArray(n.Attachments) &&
        n.Attachments.length > 0 &&
        n.Status !== 'Deleted' &&
        n.syncStatus !== 'deleted',
    );

    if (notesWithAttachments.length === 0) {
      this.loading = false;
      return;
    }

    const requests = notesWithAttachments.map((note) =>
      this.fileUploadService.getFiles(note._id, note.spaceId).pipe(catchError(() => of([] as Attachment[]))),
    );

    const spacesById = new Map(this.spaceDirectory.spaces.map((space) => [space.id, space]));
    const privateSpace = this.spaceDirectory.spaces.find((space) => space.type === 'PRIVATE');
    const me = this.auth.getUsername() ?? '';

    forkJoin(requests).subscribe({
      next: (results) => {
        const allRows: AttachmentRow[] = [];
        results.forEach((attachments, i) => {
          const note = notesWithAttachments[i];
          // No spaceId = the user's own PRIVATE Space.
          const space = note.spaceId ? spacesById.get(note.spaceId) : privateSpace;
          const spaceName = space?.name ?? (note.spaceId ? note.spaceId : 'Private');
          const owner = space?.owner_username ?? (note.spaceId ? '' : me);
          attachments.forEach((att) => {
            allRows.push({
              attachmentId: att._id,
              filename: att.filename || att._id,
              size: att.size || 0,
              mimeType: att.mimeType || '',
              noteId: note._id,
              spaceId: note.spaceId,
              noteTitle: note.Title || '(Untitled)',
              createdOn: att.created_on || '',
              spaceName,
              owner,
            });
          });
        });
        this.rows = allRows;
        this.applyFilter();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.snackbar.show('Failed to load attachments', 'error');
      },
    });
  }

  applyFilter(): void {
    const q = this.searchText.trim().toLowerCase();
    let result = q
      ? this.rows.filter(
          (r) =>
            r.filename.toLowerCase().includes(q) ||
            r.noteTitle.toLowerCase().includes(q) ||
            r.spaceName.toLowerCase().includes(q) ||
            r.owner.toLowerCase().includes(q),
        )
      : [...this.rows];

    result = this.sortRows(result);
    this.filteredRows = result;
  }

  private sortRows(rows: AttachmentRow[]): AttachmentRow[] {
    const dir = this.sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      let av: string | number;
      let bv: string | number;
      if (this.sortColumn === 'size') {
        av = a.size;
        bv = b.size;
      } else if (this.sortColumn === 'createdOn') {
        av = Date.parse(a.createdOn) || 0;
        bv = Date.parse(b.createdOn) || 0;
      } else if (this.sortColumn === 'filename') {
        av = a.filename.toLowerCase();
        bv = b.filename.toLowerCase();
      } else {
        av = a[this.sortColumn].toLowerCase();
        bv = b[this.sortColumn].toLowerCase();
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }

  sortBy(col: SortColumn): void {
    if (this.sortColumn === col) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = col;
      // Newest first is the useful default for dates.
      this.sortDir = col === 'createdOn' ? 'desc' : 'asc';
    }
    this.applyFilter();
  }

  openMenu(event: Event, row: AttachmentRow): void {
    this.activeRow = row;
    this.rowMenu.toggle(event);
  }

  isImage(row: AttachmentRow): boolean {
    return row.mimeType.startsWith('image/');
  }

  /** Filename click: an image opens in a new window, any other file is downloaded. */
  openOrDownload(row: AttachmentRow, event: Event): void {
    event.preventDefault();
    if (this.isImage(row)) {
      this.openImageInNewWindow(row);
    } else {
      this.downloadAttachment(row);
    }
  }

  private openImageInNewWindow(row: AttachmentRow): void {
    // Open the window now, while still inside the click — a window opened after the (async)
    // fetch would be blocked as a popup. The file needs the auth header, so it's shown via a blob URL.
    const win = window.open('', '_blank');
    if (win) {
      win.document.title = row.filename;
      win.document.body.style.margin = '0';
      win.document.body.textContent = 'Loading…';
    }
    this.fileUploadService
      .getFileForNote(row.noteId, row.attachmentId, row.filename, row.spaceId)
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          if (win && !win.closed) {
            win.location.href = url;
          } else {
            window.open(url, '_blank');
          }
          // Long enough for the new window to load it.
          setTimeout(() => URL.revokeObjectURL(url), 60_000);
        },
        error: () => {
          win?.close();
          this.snackbar.show('Could not open the image', 'error');
        },
      });
  }

  downloadAttachment(row: AttachmentRow): void {
    this.fileUploadService
      .getFileForNote(row.noteId, row.attachmentId, row.filename, row.spaceId)
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = row.filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        },
        error: () => this.snackbar.show('Download failed', 'error'),
      });
  }

  openRenameDialog(row: AttachmentRow): void {
    this.activeRow = row;
    this.renameValue = row.filename;
    this.renameErrorMsg = '';
    this.showRenameDialog = true;
  }

  submitRename(): void {
    if (!this.activeRow) return;
    const newName = this.renameValue.trim();
    if (!newName) {
      this.renameErrorMsg = 'Name cannot be empty.';
      return;
    }
    if (newName === this.activeRow.filename) {
      this.showRenameDialog = false;
      return;
    }

    this.renaming = true;
    const row = this.activeRow;

    this.fileUploadService
      .renameAttachment(row.attachmentId, newName, row.noteId, row.spaceId)
      .pipe(finalize(() => (this.renaming = false)))
      .subscribe({
        next: (res) => {
          if (res.status === 'success') {
            row.filename = newName;
            this.applyFilter();
            this.showRenameDialog = false;
            this.snackbar.show('Attachment renamed', 'success');
          } else {
            this.renameErrorMsg = res.error?.message || 'Rename failed.';
          }
        },
        error: (err: any) => {
          this.renameErrorMsg = err?.error?.message || err?.message || 'Rename failed.';
        },
      });
  }

  confirmDelete(row: AttachmentRow): void {
    this.confirmationService.confirm({
      message: `Delete "${row.filename}"? This cannot be undone.`,
      header: 'Delete Attachment',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteAttachment(row),
    });
  }

  private deleteAttachment(row: AttachmentRow): void {
    this.fileUploadService.deleteAttachment(row.noteId, row.attachmentId, row.spaceId).subscribe({
      next: async (res) => {
        if (res.status !== 'success') {
          this.snackbar.show(res.error?.message || 'Delete failed', 'error');
          return;
        }

        void this.dataOffline.deleteAttachment(row.attachmentId);

        // Drop it from every note that lists it or shows it as an inline image.
        const notesToUpdate = (this.dataService.allNotes || []).filter(
          (n) =>
            (Array.isArray(n.Attachments) && n.Attachments.includes(row.attachmentId)) ||
            String(n.DescriptionHtml ?? '').includes(row.attachmentId) ||
            String(n.CommentsHtml ?? '').includes(row.attachmentId),
        );
        for (const note of notesToUpdate) {
          const updated: Note = {
            ...note,
            Attachments: (note.Attachments ?? []).filter((id) => id !== row.attachmentId),
            DescriptionHtml: removeAttachmentImagesFromHtml(note.DescriptionHtml, row.attachmentId),
            CommentsHtml: removeAttachmentImagesFromHtml(note.CommentsHtml, row.attachmentId),
          };
          this.dataService.updateNote(updated).subscribe();
        }

        this.rows = this.rows.filter((r) => r.attachmentId !== row.attachmentId);
        this.applyFilter();
        this.snackbar.show('Attachment deleted', 'success');
      },
      error: (err: any) => {
        this.snackbar.show(err?.error?.message || err?.message || 'Delete failed', 'error');
      },
    });
  }

  formatSize(bytes: number): string {
    if (!bytes || bytes === 0) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  formatDate(value: string): string {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return '—';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${String(date.getFullYear()).slice(-2)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  /** Red for PDF, blue for images and Word documents, grey otherwise. */
  fileIconColor(mimeType: string): string {
    if (mimeType === 'application/pdf') return 'text-red-600';
    if (mimeType.startsWith('image/') || mimeType.includes('word')) {
      return 'text-blue-600';
    }
    return 'text-gray-400';
  }

  fileIcon(mimeType: string): string {
    if (!mimeType) return 'pi pi-file';
    if (mimeType.startsWith('image/')) return 'pi pi-image';
    if (mimeType === 'application/pdf') return 'pi pi-file-pdf';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'pi pi-file-word';
    if (mimeType.includes('sheet') || mimeType.includes('excel')) return 'pi pi-file-excel';
    return 'pi pi-file';
  }

  back(): void {
    this.router.navigate(['/manage']);
  }
}
